import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithInfiniteQuery, atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import type { SendGateConfigDto } from '@/modules/sentinel-workspace/types';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { isAuthenticatedAtom } from './use-auth';
import {
	deviationService,
	type DeviationFilter,
	type DeviationFilterOptions,
	type DeviationPage,
	type DeviationReviewVerdict
} from '@/services/deviation.service';

const QUERY_ROOT = 'deviations';

const deviationsQueryAtom = (filter: DeviationFilter) =>
	atomWithInfiniteQuery((get) => ({
		// The brand is in the key: two brands never read each other's rows.
		queryKey: [QUERY_ROOT, 'list', buScopeKey(get), filter],
		queryFn: ({ pageParam }: { pageParam: string | undefined }) => deviationService.fetchPage(filter, pageParam, buScopeParams(get)),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage: DeviationPage) => (lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined),
		enabled: get(isAuthenticatedAtom)
	}));

/** The selected brand's deviations, in the filter's order, a page at a time. */
export function useDeviations(filter: DeviationFilter) {
	// `filter` is memoised by the caller, so a new atom (and query) only when it really changes.
	const queryAtom = useMemo(() => deviationsQueryAtom(filter), [filter]);
	const { data, isLoading, isError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } = useAtomValue(queryAtom);
	const entries = useMemo(() => data?.pages.flatMap((page) => page.entries) ?? [], [data]);
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);
	return {
		entries,
		isLoading,
		isError,
		refetch,
		hasMore: Boolean(hasNextPage),
		loadingMore: isFetchingNextPage,
		loadMore
	};
}

const NO_OPTIONS: DeviationFilterOptions = { senders: [], intents: [] };

const filterOptionsQueryAtom = (window: Pick<DeviationFilter, 'from' | 'to'>) =>
	atomWithQuery((get) => ({
		// The brand is in the key: each brand lists its own senders and intents.
		queryKey: [QUERY_ROOT, 'filter-options', buScopeKey(get), window],
		queryFn: () => deviationService.fetchFilterOptions(window, buScopeParams(get)),
		enabled: get(isAuthenticatedAtom)
	}));

/**
 * The selected brand's Sent by and Intent options in the date window. Called from inside the Filters
 * popover, so it is asked for only while the popover is open.
 */
export function useDeviationFilterOptions(window: Pick<DeviationFilter, 'from' | 'to'>): DeviationFilterOptions {
	const { from, to } = window;
	const queryAtom = useMemo(() => filterOptionsQueryAtom({ from, to }), [from, to]);
	return useAtomValue(queryAtom).data ?? NO_OPTIONS;
}

const detailQueryAtom = (checkId: string | null) =>
	atomWithQuery((get) => ({
		queryKey: [QUERY_ROOT, 'detail', buScopeKey(get), checkId],
		queryFn: () => deviationService.fetchDetail(checkId!, buScopeParams(get)),
		enabled: get(isAuthenticatedAtom) && checkId != null
	}));

/** One deviation's drawer; nothing is asked for while no row is open. */
export function useDeviationDetail(checkId: string | null) {
	const queryAtom = useMemo(() => detailQueryAtom(checkId), [checkId]);
	const { data, isLoading, isError, refetch } = useAtomValue(queryAtom);
	return {
		detail: data ?? null,
		isLoading: checkId != null && isLoading,
		isError,
		refetch
	};
}

const saveReviewAtom = atomWithMutation((get) => ({
	mutationKey: [QUERY_ROOT, 'review'],
	mutationFn: ({ checkId, verdict, note }: { checkId: string; verdict: DeviationReviewVerdict; note: string | null }) =>
		deviationService.saveReview(checkId, { verdict, note }, buScopeParams(get)),
	// The row's Reviewed state and the drawer's saved review both come from the server.
	onSettled: () => invalidateQueries([QUERY_ROOT])
}));

/** Save a reviewer's verdict on a deviation; the log and the drawer refresh after. */
export const useSaveDeviationReview = () => useAtom(saveReviewAtom)[0];

/** Sentinel Workspace settings invalidates this root when it saves the gate. */
export const SEND_GATE_QUERY_ROOT = ['sentinel', 'send-gate'];

const sendGateQueryAtom = (allowed: boolean) =>
	atomWithQuery((get) => ({
		queryKey: [...SEND_GATE_QUERY_ROOT, buScopeKey(get)],
		queryFn: () => sentinelWorkspaceService.getSendGate(buScopeParams(get)),
		// Not before the brand resolves: unscoped, the backend answers with the account default instead.
		enabled: allowed && get(isAuthenticatedAtom) && get(selectedBusinessUnitIdAtom) !== null
	}));

/**
 * The selected brand's pre-send quality gate, so Deviations can say when it is off. Null while it loads,
 * if it fails to load, or when `allowed` is false (the endpoint needs view access to the Sentinel workspace).
 */
export function useSendGateConfig(allowed: boolean): SendGateConfigDto | null {
	const queryAtom = useMemo(() => sendGateQueryAtom(allowed), [allowed]);
	return useAtomValue(queryAtom).data ?? null;
}
